import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import WheelLoader from '../common/WheelLoader'
import GoogleButton from './GoogleButton'
import PasswordRequirements from './PasswordRequirements'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialView?: 'signin' | 'signup'
  onPauseSlideshow?: (paused: boolean) => void
}

export default function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  initialView = 'signin',
  onPauseSlideshow,
}: AuthModalProps) {
  const [view, setView] = useState<'signin' | 'signup'>(initialView)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const { login, register } = useAuth()

  // Reset view when initialView changes
  useEffect(() => {
    setView(initialView)
  }, [initialView])

  // Handle body scroll lock and slideshow pause
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      onPauseSlideshow?.(true)
    } else {
      document.body.style.overflow = ''
      onPauseSlideshow?.(false)
    }
    return () => {
      document.body.style.overflow = ''
      onPauseSlideshow?.(false)
    }
  }, [isOpen, onPauseSlideshow])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const resetForm = () => {
    setEmail('')
    setPassword('')
    setPasswordConfirmation('')
    setError('')
  }

  const toggleView = () => {
    setIsTransitioning(true)
    setTimeout(() => {
      setView(view === 'signin' ? 'signup' : 'signin')
      resetForm()
      setIsTransitioning(false)
    }, 150)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (view === 'signup') {
      if (password !== passwordConfirmation) {
        setError('Passwords do not match')
        return
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters')
        return
      }
    }

    setLoading(true)

    try {
      if (view === 'signin') {
        await login(email, password)
      } else {
        await register(email, password, passwordConfirmation)
      }
      resetForm()
      onSuccess()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { errors?: { full_messages?: string[] } } } }
      if (view === 'signin') {
        setError('Invalid email or password')
      } else {
        setError(error.response?.data?.errors?.full_messages?.join(', ') || 'Registration failed.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSuccess = () => {
    resetForm()
    onSuccess()
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-md" onClick={handleClose} />

      {/* Modal */}
      <div
        className="relative bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl w-full max-w-sm mx-4 max-h-[90vh] overflow-y-auto"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-black/40 hover:text-black/60 transition-colors z-10"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div
          className={`p-5 sm:p-6 transition-opacity duration-150 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
        >
          {/* Header */}
          <div className="text-left mb-3">
            <h2
              className="text-2xl text-black tracking-normal"
              style={{ fontFamily: "'Instrument Serif', serif", lineHeight: '1.1' }}
            >
              {view === 'signin' ? 'Welcome back' : 'Create an account'}
            </h2>
            <p className="text-black/60 mt-1 text-sm">
              {view === 'signin'
                ? 'Sign in to manage your profile'
                : 'Sign up to create your website'}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300">
              <p className="text-xs text-red-800 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </p>
            </div>
          )}

          {/* Google Button */}
          <div className="mb-3">
            <GoogleButton
              mode={view === 'signin' ? 'signin' : 'signup'}
              onSuccessCallback={handleGoogleSuccess}
            />
          </div>

          <p className="text-xs text-black/50 text-center mb-3">or continue with email</p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label htmlFor="modal-email" className="block text-sm font-medium text-black/70 mb-1">
                Email
              </label>
              <input
                id="modal-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label
                htmlFor="modal-password"
                className="block text-sm font-medium text-black/70 mb-1"
              >
                Password
              </label>
              <input
                id="modal-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
                className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
                placeholder="Min. 6 characters"
              />
            </div>

            {view === 'signup' && (
              <div>
                <label
                  htmlFor="modal-password-confirm"
                  className="block text-sm font-medium text-black/70 mb-1"
                >
                  Confirm password
                </label>
                <input
                  id="modal-password-confirm"
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  required
                  className="block w-full px-3 py-2.5 border border-black/20 rounded-xl text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all bg-white/50 text-sm"
                  placeholder="Re-enter your password"
                />
                <PasswordRequirements
                  password={password}
                  passwordConfirmation={passwordConfirmation}
                  showConfirmation={true}
                />
              </div>
            )}

            {view === 'signin' && (
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  onClick={handleClose}
                  className="text-xs text-black/60 hover:text-black transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <WheelLoader width={24} />
                  {view === 'signin' ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : view === 'signin' ? (
                'Sign in'
              ) : (
                'Create account'
              )}
            </button>
          </form>

          {/* Toggle view */}
          <div className="mt-4 pt-4 border-t border-black/10">
            <p className="text-center text-sm text-black/60">
              {view === 'signin' ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button
                type="button"
                onClick={toggleView}
                className="font-medium text-black hover:text-black/70 transition-colors"
              >
                {view === 'signin' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Keyframe animation */}
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  )
}
